import type { SPIntl } from 'contexts/intl/intlContext';
import type { IntlKey } from 'locales';
import { sortBy } from 'lodash-es';
import type { ProductVariation, ProductVariationLabel } from 'models/productsReferential';

export function filterSearchResult(
  searchedText: string,
  availableProductVariations: readonly ProductVariation[],
  formatMessage: SPIntl['formatMessage'],
): ProductVariation[] {
  // Translate product variation to handle i18n use case
  let productVariationWithLabel: ProductVariationLabel[] = translateProductVariation(
    availableProductVariations,
    formatMessage,
  );

  // Match if searched text is similar to "A B C", otherwise we use the normal algo
  const shortMatchRegex: RegExp = /^\s*([a-zA-Z])\s{1,}([a-zA-Z])\s{1,}([a-zA-Z])\s*$/;
  if (shortMatchRegex.test(searchedText)) {
    productVariationWithLabel = searchProductVariationWithFirstCharacter(searchedText, productVariationWithLabel);
  } else {
    productVariationWithLabel = searchProductVariationWithLabel(searchedText, productVariationWithLabel);
  }

  const sortedProductVariationWithLabel = sortBy(productVariationWithLabel, [
    'productTypeLabel',
    'productSubTypeLabel',
    'wrapperLabel',
  ]);

  return sortedProductVariationWithLabel.map(
    ({ productType, commercialProductType, productSubType, commercialProductSubType, wrapper, requestType }) => ({
      productType,
      commercialProductType,
      productSubType,
      commercialProductSubType,
      wrapper,
      requestType,
    }),
  );
}

function searchProductVariationWithFirstCharacter(
  searchedText: string,
  productVariationWithLabel: ProductVariationLabel[],
) {
  const [searchedProductType, searchedProductSubType, searchedWrapper] = searchedText.replace(/\s/g, '').split('');

  const x = productVariationWithLabel.filter(
    variation =>
      variation.productTypeLabel?.toLowerCase()?.charAt(0) === searchedProductType.toLowerCase() &&
      variation.productSubTypeLabel?.toLowerCase()?.charAt(0) === searchedProductSubType.toLowerCase() &&
      variation.wrapperLabel?.toLowerCase()?.charAt(0) === searchedWrapper.toLowerCase(),
  );
  return x;
}

function searchProductVariationWithLabel(searchedText: string, productVariationWithLabel: ProductVariationLabel[]) {
  const searchedTextList = searchedText
    .replace(/[^a-zA-Z ]/g, '')
    .toLowerCase()
    .split(' ');

  for (let searchIndex = 0; searchIndex < searchedTextList.length; searchIndex++) {
    const searchedText = searchedTextList[searchIndex];
    if (searchedText !== '') {
      const tempResult: ProductVariation[] = [];
      for (let variationIndex = 0; variationIndex < productVariationWithLabel.length; variationIndex++) {
        const variation = productVariationWithLabel[variationIndex];
        if (
          variation.productTypeLabel?.toLowerCase()?.includes(searchedText) ||
          variation.productSubTypeLabel?.toLowerCase()?.includes(searchedText) ||
          variation.wrapperLabel?.toLowerCase()?.includes(searchedText)
        ) {
          tempResult.push(variation);
        }
      }
      productVariationWithLabel = tempResult;
    }
  }
  return productVariationWithLabel;
}

function translateProductVariation(
  availableProductVariations: readonly ProductVariation[],
  formatMessage: SPIntl['formatMessage'],
): ProductVariationLabel[] {
  return availableProductVariations.map(
    ({ productType, commercialProductType, productSubType, commercialProductSubType, wrapper, requestType }) => ({
      productType,
      commercialProductType,
      productSubType,
      commercialProductSubType,
      wrapper,
      requestType,
      productTypeLabel:
        commercialProductType ??
        formatMessage({
          id: `quote.products.${productType}` as IntlKey,
          defaultMessage: productType,
        }),
      productSubTypeLabel:
        commercialProductSubType ??
        formatMessage({
          id: `quote.products.${productSubType}` as IntlKey,
          defaultMessage: productSubType,
        }),
      wrapperLabel: formatMessage({
        id: `quote.products.${wrapper}` as IntlKey,
        defaultMessage: wrapper,
      }),
    }),
  );
}
